import React, { useEffect, useState } from 'react'
import type { AuthContextValue } from './AuthProvider'
import { useAuth } from './AuthProvider'
import { getLoginAuthorizationParams, storeAuthRedirectUrl } from './util/login'

/**
 * Automatically redirects to login if the user is unauthenticated
 *
 * There used to be a second "refresh session if possible on page load" effect here that called
 * `authenticate({ ignoreCache: false })` and, on failure, redirected to the login form. It could
 * never run: `AuthProvider` initialises its reducer with `isAuthenticating: true` unconditionally,
 * and that effect had `[]` deps behind an `if (!isAuthenticating)` guard, so on the single run it
 * ever got the guard was always false. #22308 fixed its error handling without noticing it was
 * unreachable, which is why that PR did not fix ENG-1190; its specs only passed because they
 * mocked `useAuth` and injected `isAuthenticating: false`.
 *
 * It is deleted rather than revived: production has run without it for a long time, a silent
 * `getTokenSilently` on every page load is exactly the call that hangs for ~60s when the iframe
 * fallback is blocked, and the token the app actually uses is minted (and time-boxed) by the
 * Apollo auth link on each request. Callers that need fresh claims already ask for them
 * explicitly via `authenticate({ ignoreCache: true })`.
 */
export default function Authorizer({
  children
}: {
  children: React.ReactNode | ((args: AuthContextValue) => React.ReactNode)
}) {
  const ctx = useAuth()
  const { auth0Client, session, initialized } = ctx
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    // `initialized` gate: before AuthProvider's initializeAuth settles, a null session only means
    // the async check hasn't finished — most importantly the bounded silent-token check that
    // revives a session whose 300s ID token has expired (ENG-1190). Redirecting on that first
    // effect pass sent users through a full Auth0 round trip that the check would have avoided.
    // Deliberately NOT gated on `isAuthenticating`: that stays true when init finds no session
    // (admin/painter-view render their loading screens off it), so gating on it would leave a
    // fresh visitor with no redirect at all.
    if (initialized && !redirecting && !session) {
      setRedirecting(true)
      storeAuthRedirectUrl()
      auth0Client
        .loginWithRedirect({
          authorizationParams: getLoginAuthorizationParams()
        })
        .catch((e) => {
          // Without this the rejection is unhandled and the user is left on a spinner with
          // nothing in the console to explain it. No automatic retry: `redirecting` stays true
          // deliberately, since resetting it would re-run this effect immediately and spin.
          // eslint-disable-next-line no-console
          console.error('[react-auth0] loginWithRedirect failed', e)
        })
    }
    // eslint-disable-next-line
  }, [redirecting, session, initialized])

  return <>{typeof children === 'function' ? children(ctx) : children}</>
}
